import { Component, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, ReplaySubject, BehaviorSubject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as pluralize from 'pluralize';

import { BrokerService } from '@c4/core';

import { ItemsService } from './../../../common/services/items.service';
import { Item } from './../../../common/interfaces/item.interface';
import { Command } from '../../../common/params/interfaces/command.interface';
import { Parameter } from '../../../common/params/interfaces/parameter.interface';
import { Device } from '../../../common/interfaces/item.interface';
import { DeviceEvent } from '../../../common/interfaces/event.interface';
import { SharedProgrammingService, CommandData, CommonProgrammingContext } from '../../../common/services/shared-programming.service';
import { ProgrammingUtilsService as Util } from '../../../common/services/utils.service';
import { BaseRoutingComponent } from '../../../common/base-routing/base-routing.component';

import { Room, MediaProgrammingContext, QSMediaService } from '../quickstart-media.service';

interface Config {
  [key: string]: {
    display: string;
    icon: string;
    waitText: string;
    command: string;
    endpoint: string;
  }
}

const CONFIG: Config = {
  broadcast_audio: {
    display: 'Select Audio Source to play',
    icon: 'music',
    waitText: 'Finding audio sources...',
    command: 'SELECT_AUDIO_MEDIA:BROADCAST_AUDIO',
    endpoint: 'broadcast_audio'
  },
  broadcast_video: {
    display: 'Select Video Source to play',
    icon: 'watch',
    waitText: 'Finding video sources...',
    command: 'SELECT_VIDEO_MEDIA:BROADCAST_VIDEO',
    endpoint: 'broadcast_video'
  },
  movie: {
    display: 'Select Movie to play',
    icon: 'watch',
    waitText: 'Finding movies...',
    command: 'SELECT_AUDIO_MEDIA:MOVIE',
    endpoint: 'movies'
  },
  playlist: {
    display: 'Select Playlist to play',
    icon: 'music',
    waitText: 'Finding playlists...',
    command: 'SELECT_AUDIO_MEDIA:PLAYLIST',
    endpoint: 'albums/playlists'
  },
  album: {
    display: 'Select Album to play',
    icon: 'music',
    waitText: 'Finding albums...',
    command: 'SELECT_AUDIO_MEDIA:ALBUM',
    endpoint: 'albums'
  }
}

interface Filter {
  id: string;
  name: string;
  default: string;
  options: any[];
  selected?: string;
}

interface Channel {
  id: number;
  device_id: number;
  genre: string;
  description: string;
  name?: string;
  title?: string;
  artist?: string;
  source?: Device
}

@Component({
  templateUrl: './select-media.component.html',
  styleUrls: ['./select-media.component.less'],
  host: {
    '(document:click)': 'onClick($event)',
  }
})
export class QSMediaSelectMediaComponent extends BaseRoutingComponent {

  private _sourceName: Observable<string>;
  private _roomId: Observable<number>;
  private _room: Observable<Room>;
  private _params: Observable<Parameter[]>;
  private _channels: ReplaySubject<Channel[]>;
  private _filteredChannels: Observable<Channel[]>;
  private _command: Observable<Command>;
  private _waitText: Observable<string>;
  private _config = CONFIG;
  private _filters: ReplaySubject<Filter[]>;
  private _sources: Device[];

  private _showFilter: boolean = false;

  constructor(
    private _route: ActivatedRoute,
    private _broker: BrokerService,
    protected store: Store<{
      mediaProgramming: MediaProgrammingContext,
      sharedProgramming: CommonProgrammingContext
    }>,
    private _shared: SharedProgrammingService,
    public router: Router,
    private _service: QSMediaService,
    private _elementRef: ElementRef,
    private _items: ItemsService
  ) {
    super();

    this._sourceName = this._route.params.map((params: { source: string }) => params.source);

    this._roomId = this._route.params.map((params: { roomId: string }) => parseInt(params.roomId));

    this._room = this._roomId.flatMap(roomId => {
      return this.store.select(s => s.mediaProgramming.rooms)
        .filter(r => !!r)
        .map(rooms => rooms.find(room => room.id == roomId))
    });

    this._command = Observable.combineLatest(
      this._room,
      this._sourceName,
      (room, source) => room.commands.find(c => c.command == CONFIG[source].command)
    );

    this._filters = new ReplaySubject();

    this._channels = new ReplaySubject();

    Observable.combineLatest(
      this._sourceName,
      this._room
    )
      .flatMap(([sourceName, room]) => <Promise<Channel[]>>this._broker.call({
        path: `/api/v1/locations/rooms/${room.id}/media/${CONFIG[sourceName].endpoint}`
      }))
      // We need to get the name of the device
      // providing the media, the simplest way
      // is to query for the devices associated
      // with each channel. NOTE: This may have
      // a prohibitive performance cost. If it
      // turns out to be too expensive, this will
      // need to be rethought.
      .flatMap(channels => {
        // TODO figure out how to integrate items service/subscription here
        return Observable.fromPromise(<Promise<Device[]>>this._broker.call({
          path: '/api/v1/items',
          queryString: {
            query: {
              id: {
                '$in': channels.map(c => c.device_id)
              }
            }
          }
        }))
          .map(devices => channels.map(channel => {
            channel.source = devices.find(d => d.id == channel.device_id);
            return channel;
          }));
      })
      .subscribe(channels => this._channels.next(channels));


    // EXPERIMENTAL
    this._filteredChannels = Observable.combineLatest(
      this._channels,
      this._filters,
      (channels, filters) => {
        return channels.filter(channel => {
          return filters
            .filter(f => !!f.selected && f.default != f.selected)
            .every(f => {
              return channel[f.id] == f.selected;
            })
        });
      }
    );

    // EXPERIMENTAL
    // Dynamically build filters. This iterates
    // over our collection of channels, watching
    // for string fields that are frequently the
    // same value across channel. e.g. genre is
    // likely to have a relatively small number
    // of discrete values.
    this._channels
      .filter(c => !!c)

      // Pull the names of fields common
      // to ALL channels.
      .map(channels => {
        return channels
          .map(channel => Object.keys(channel).sort())
          .reduce((prev, curr) => {
            prev = prev || [...curr];
            let accumulator = [];
            while (prev.length > 0 && curr.length > 0) {
              if (prev[0] < curr[0]) { prev.shift(); }
              else if (prev[0] > curr[0]) { curr.shift(); }
              else {
                accumulator.push(prev.shift());
                curr.shift();
              }
            }
            return accumulator;
          }, [])
      })

      // For each key, find ALL values for
      // that key across all channels
      .flatMap(keys => {
        return this._channels.map(channels => {
          return channels.reduce((acc, curr) => {
            keys.map(key => {
              acc[key] = acc[key] || [];
              if (acc[key].indexOf(curr[key]) == -1) {
                acc[key].push(curr[key])
              }
            });

            return acc;
          }, {})
        })
      })

      // Create a filter for each
      // of the keys that meets our requirements
      .map((values: {
        [key: string]: any[]
      }) => {
        let filters: Filter[] = [];

        Object.keys(values).map(key => {
          let vals = values[key];
          if (
            !!vals &&                         // There must be values for the key
            vals.length > 1 &&                // There must be more than one distinct value
            vals.length < 20 &&               // But no more than 20 (to keep the filter options manageable)
            typeof vals[0] == 'string' &&     // Only string types
            vals.every(v => v.length < 30)) { // So we don't get filters for ridiculously long values
            filters.push({
              id: key,
              name: this._pretty(key),
              default: `All ${pluralize(this._pretty(key))}`,
              options: vals.sort()
            })
          }
        })

        return filters;
      }).take(1).subscribe(filters => {
        this._filters.next(filters);
      });

    this._waitText = this._sourceName.map(cn => this._config[cn].waitText);
  }

  /**
   * Capitalize each word in a string,
   * used for filter names
   */
  private _pretty = (str: string): string => {
    let tokens = str.split(/[\s,]+/);
    return tokens.map(t => {
      return t.charAt(0).toUpperCase() + t.slice(1);
    }).join(' ');
  }

  // NOTE unfortunately coupling to the UI here, but we need to stop propagation of the click event that
  // calls this, otherwise the parent click handler will also get invoked, which will keep the filter menu
  // open
  private _setFilter(event: MouseEvent, id: string, selected: string) {
    event.stopPropagation();

    this._filters.take(1).subscribe(filters => {
      let newState = [...filters];
      let ix = newState.findIndex(f => f.id == id);
      newState[ix].selected = selected;
      this._filters.next(newState);
    });

    this.hideFilter();
  }

  private _setAction(selection: Channel) {
    this._command
      .take(1)
      .map((command): CommandData => {
        return {
          codeitemId: 0,
          deviceId: command.deviceId,
          command: command.command,
          beforeCodeItemId: 0,
          parentCodeItemId: 0,
          creator: '',
          type: 1,
          params: command.params.map(p => {
            let param = {
              name: p.name,
              value: p.value || selection.id,
              display: undefined
            }

            p.valueDisplay = (command.command == "SELECT_AUDIO_MEDIA:PLAYLIST" && p.name == "mediaid") ? "{title}" : p.valueDisplay;
            param.display = (!!p.valueDisplay) ? QSMediaService.composeField(p.valueDisplay, selection) : undefined;

            return param;
          })
        }
      })
      .flatMap(commandData => this._shared.saveAction(commandData))
      .subscribe();
  }

  toggleFilter() {
    this._showFilter = !this._showFilter;
  }

  hideFilter() {
    this._showFilter = false;
  }

  // close tbe drop down menu if the user clicks anywhere outside the header
  onClick = (event: MouseEvent) => {
    if (!this._elementRef.nativeElement.contains(event.target)) {
      this.hideFilter();
    }
  }
}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/quickstarts/quickstart-media/select-media/select-media.component.ts